import type { SetupScreen, SetupStepNavigator } from './setup-screen'

/** The content-strategy screens, in order. The index of a screen is its place in setup. */
export const CONTENT_STRATEGY_SCREENS = [
  { id: 'reading', transient: true },
  { id: 'strategy', transient: false },
] as const satisfies readonly SetupScreen[]

/** One content-strategy screen. A failed draft is the failed variant of `reading`. */
export type ContentStrategyScreen = (typeof CONTENT_STRATEGY_SCREENS)[number]['id']

export type ContentStrategyMove =
  /** The draft on show has finished and the ring has burst. */
  | 'draft-landed'
  /** Write again on the strategy page has started a new draft. */
  | 'draft-opened'

/** What the saved data holds, reduced to what the first screen turns on. */
export interface ContentStrategyResumeState {
  /** True when a Belief Map is saved. */
  readonly hasBeliefMap: boolean
  /** True when a Content Strategy is saved. */
  readonly hasStrategy: boolean
  /** True while the latest draft is still working. */
  readonly runLive: boolean
  /** True when the latest draft started after the step before was last confirmed. */
  readonly current: boolean
  /** True when the current draft failed; the ring shows it with Retry. */
  readonly runFailed: boolean
}

/** The screen the step opens on, taken once from the saved data. */
export function resumeContentStrategyScreen(
  state: ContentStrategyResumeState,
): ContentStrategyScreen {
  return state.hasBeliefMap &&
    state.hasStrategy &&
    state.current &&
    !state.runLive &&
    !state.runFailed
    ? 'strategy'
    : 'reading'
}

/** The screen a move leads to. A move that does not apply to the screen on show keeps it. */
export function moveContentStrategy(
  from: ContentStrategyScreen,
  move: ContentStrategyMove,
): ContentStrategyScreen {
  switch (move) {
    case 'draft-landed':
      return from === 'reading' ? 'strategy' : from
    case 'draft-opened':
      return from === 'strategy' ? 'reading' : from
  }
}

export const CONTENT_STRATEGY_NAVIGATOR: SetupStepNavigator<
  ContentStrategyScreen,
  ContentStrategyMove
> = {
  screens: CONTENT_STRATEGY_SCREENS,
  move: moveContentStrategy,
}
